import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import BackLink from "../components/BackLink";
import { api, recordVisit } from "../lib/api";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import {
  ChevronLeft, ChevronRight, CheckCircle2, Circle, Bookmark, BookmarkCheck,
  Headphones, BookOpen, Search, Menu, X, Landmark, Sparkles, Lightbulb,
  ScrollText, ListChecks, NotebookPen, Loader2, GraduationCap, ChevronDown,
  Play, Pause, Sunrise, Star, History, HandHeart, ThumbsUp, Ban,
} from "lucide-react";

/* ============ Tafsir Reader — book-like thematic tafsir experience ============ */

export default function TafsirReader() {
  const { surah } = useParams();
  const surahNum = parseInt(surah);
  const { tx, t: tr, lang } = useLanguage(); // content is served in this language; refetch when it changes
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);
  const [progress, setProgress] = useState({}); // {section: {completed, bookmarked, note}}
  const [active, setActive] = useState(params.get("section") || null); // null => intro
  const [section, setSection] = useState(null); // loaded section content
  const [loading, setLoading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [checked, setChecked] = useState({}); // life application checklist (local)

  useEffect(() => {
    api.get(`/tafsir/surah/${surahNum}`)
      .then((r) => setMeta(r.data))
      .catch(() => { toast.error(tr("tafsirReader.thisSurahIsnTIn")); navigate("/app/tadabbur"); });
    refreshProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surahNum, lang]);

  const refreshProgress = () => {
    api.get(`/tafsir/progress/${surahNum}`).then((r) => {
      const map = {};
      r.data.forEach((p) => { map[p.section] = p; });
      setProgress(map);
    }).catch(() => {});
  };

  // Legacy deep link ?ayah=N → open the section containing that ayah
  useEffect(() => {
    const ayah = parseInt(params.get("ayah"));
    if (!meta || !ayah || params.get("section")) return;
    const hit = meta.sections.find((s) => ayah >= s.start && ayah <= s.end);
    if (hit) selectSection(hit.slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  // Load the active section's content
  useEffect(() => {
    if (!active) { setSection(null); return; }
    setLoading(true);
    setSection(null);
    setChecked({});
    api.get(`/tafsir/surah/${surahNum}/section/${active}`)
      .then((r) => setSection(r.data))
      .catch(() => toast.error(tr("tafsirReader.couldnTLoadThisSection")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surahNum, active, lang]);

  // Track visits so the dashboard Continue strip can resume here
  useEffect(() => {
    if (!meta) return;
    const sec = meta.sections.find((s) => s.slug === active);
    const t = setTimeout(() => recordVisit({
      kind: "tadabbur",
      surah: surahNum,
      ayah: sec ? sec.start : 1,
      ayah_end: sec ? sec.end : null,
      path: `/app/tadabbur/${surahNum}${active ? `?section=${active}` : ""}`,
    }), 1500);
    return () => clearTimeout(t);
  }, [meta, active, surahNum]);

  const selectSection = (slug) => {
    setActive(slug);
    setNavOpen(false);
    setParams(slug ? { section: slug } : {}, { replace: true });
    // The app scrolls inside AppLayout's #app-scroll frame, not the window.
    document.getElementById("app-scroll")?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // C9 — `targetSection` lets the sidebar list mark ANY row complete
  // without first navigating to it (defaults to the currently-open section,
  // which is all the existing bookmark/note call sites below ever needed).
  const saveProgress = async (payload, targetSection) => {
    try {
      await api.post("/tafsir/progress", { surah: surahNum, section: targetSection ?? (active || "intro"), ...payload });
      refreshProgress();
    } catch { toast.error(tr("memorize.couldnTSave")); }
  };

  const filteredSections = useMemo(() => {
    if (!meta) return [];
    const q = query.trim().toLowerCase();
    if (!q) return meta.sections;
    return meta.sections.filter((s) =>
      s.title.toLowerCase().includes(q) ||
      `${s.start}-${s.end}`.includes(q) || `${s.start}–${s.end}`.includes(q) ||
      (s.keywords || []).some((k) => k.includes(q)),
    );
  }, [meta, query]);

  if (!meta) {
    return (
      <div className="max-w-4xl mx-auto space-y-4 animate-pulse-soft" data-testid="tafsir-reader-skeleton">
        <div className="h-4 w-40 bg-sand-200 rounded" />
        <div className="hifz-card h-72 bg-sand-200/50" />
      </div>
    );
  }

  const idx = meta.sections.findIndex((s) => s.slug === active);
  const prev = active ? (idx > 0 ? meta.sections[idx - 1] : null) : null;
  const next = active ? (idx < meta.sections.length - 1 ? meta.sections[idx + 1] : null) : meta.sections[0];
  const prog = progress[active || "intro"] || {};

  return (
    <div className="max-w-7xl mx-auto" data-testid="tafsir-reader">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        {/* C5 — this used to always be a BackLink straight to "/app/tadabbur"
            (all surahs), regardless of whether you were on this surah's
            Introduction or deep inside one of its sections — so leaving a
            section skipped the surah's own overview entirely and dropped
            you all the way out to the full list. Now it's two levels,
            matching how you actually got here: from a section, back goes
            to this surah's Introduction (selectSection(null) — a plain
            in-app state change, not a route navigation, so BackLink's
            history-based logic doesn't apply here); only from the
            Introduction does back exit to the all-surahs list. */}
        {active ? (
          <button
            type="button"
            onClick={() => selectSection(null)}
            className="text-xs text-foreground/70 hover:underline inline-flex items-center gap-1"
            data-testid="tafsir-back"
          >
            <ChevronLeft className="h-4 w-4" /> {tr("tafsirReader.introduction")}
          </button>
        ) : (
          <BackLink to="/app/tadabbur" label={tr("surahView.allSurahs")} className="text-xs text-foreground/70 hover:underline inline-flex items-center gap-1" testid="tafsir-back" />
        )}
        <div className="flex items-center gap-2">
          <span className="text-sm font-display font-semibold text-forest-700">{meta.name_en}</span>
          <span className="ayah-arabic text-lg text-foreground/80">{meta.name_ar}</span>
        </div>
        <button
          onClick={() => setNavOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs border border-sand-300 hover:bg-sand-100"
          data-testid="tafsir-nav-toggle"
        >
          {navOpen ? <X className="h-3.5 w-3.5" /> : <Menu className="h-3.5 w-3.5" />} {tr("tafsirReader.sectionsToggle")}
        </button>
      </div>

      <div className="grid gap-8 items-start">
        {/* Left navigation — always the collapsible/toggled version now
            (the "Sections" button above), since the app no longer has a
            wide-desktop branch to permanently show it in a side rail. */}
        <aside className={`${navOpen ? "block" : "hidden"} space-y-3`} data-testid="tafsir-nav">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tr("tafsirReader.searchThemesTopicsVerses")}
              className="w-full rounded-xl border border-sand-300 bg-white pl-9 pr-3 py-2 text-sm"
              data-testid="tafsir-search"
            />
          </div>
          <nav className="hifz-card p-2 max-h-[70vh] overflow-y-auto space-y-0.5">
            <button
              onClick={() => selectSection(null)}
              className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition flex items-center gap-2.5 ${
                !active ? "bg-forest-600 text-sand-100" : "hover:bg-sand-200 text-foreground/85"
              }`}
              data-testid="tafsir-nav-intro"
            >
              <Landmark className="h-4 w-4 shrink-0" />
              <span className="flex-1 font-medium">{tr("tafsirReader.introduction")}</span>
              {/* C9 — the tick actually toggles completion now; stopPropagation
                  so tapping it doesn't also trigger the row's own navigate. */}
              <span
                role="button"
                tabIndex={0}
                aria-label={progress.intro?.completed ? tr("tafsirReader.markIntroductionAsNotRead") : tr("tafsirReader.markIntroductionAsRead")}
                aria-pressed={!!progress.intro?.completed}
                onClick={(e) => { e.stopPropagation(); saveProgress({ completed: !progress.intro?.completed }, "intro"); }}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  e.preventDefault(); e.stopPropagation();
                  saveProgress({ completed: !progress.intro?.completed }, "intro");
                }}
                className="shrink-0 -m-1 p-1 rounded-full cursor-pointer"
                data-testid="tafsir-nav-intro-tick"
              >
                {progress.intro?.completed
                  ? <CheckCircle2 className={`h-4 w-4 ${!active ? "text-gold-400" : "text-emerald-600"}`} />
                  : <Circle className={`h-4 w-4 ${!active ? "text-sand-100/40" : "text-sand-300"}`} />}
              </span>
            </button>
            {filteredSections.map((s) => {
              const p = progress[s.slug] || {};
              const isActive = active === s.slug;
              return (
                <button
                  key={s.slug}
                  onClick={() => selectSection(s.slug)}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition flex items-start gap-2.5 ${
                    isActive ? "bg-forest-600 text-sand-100" : "hover:bg-sand-200 text-foreground/85"
                  }`}
                  data-testid={`tafsir-nav-${s.slug}`}
                >
                  <span className={`text-[11px] font-semibold mt-0.5 shrink-0 w-12 ${isActive ? "text-gold-400" : "text-gold-600"}`}>
                    {s.start === s.end ? s.start : tr("tafsirReader.text", { start: s.start, end: s.end })}
                  </span>
                  <span className="flex-1 leading-snug">{tx(s.title)}</span>
                  <span className="shrink-0 flex items-center gap-1 mt-0.5">
                    {p.bookmarked && <Bookmark className={`h-3.5 w-3.5 ${isActive ? "text-gold-400" : "text-gold-600"}`} />}
                    {/* C9 — was purely decorative (nested inside the row's
                        own onClick, so tapping it just navigated like the
                        rest of the row and never touched `completed`).
                        stopPropagation so this toggle doesn't also select
                        the section out from under the reader's finger. */}
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={p.completed ? tr("tafsirReader.markAsNotRead", { title: s.title }) : tr("tafsirReader.markAsRead", { title: s.title })}
                      aria-pressed={!!p.completed}
                      onClick={(e) => { e.stopPropagation(); saveProgress({ completed: !p.completed }, s.slug); }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter" && e.key !== " ") return;
                        e.preventDefault(); e.stopPropagation();
                        saveProgress({ completed: !p.completed }, s.slug);
                      }}
                      className="-m-1 p-1 rounded-full cursor-pointer"
                      data-testid={`tafsir-nav-tick-${s.slug}`}
                    >
                      {p.completed
                        ? <CheckCircle2 className={`h-4 w-4 ${isActive ? "text-gold-400" : "text-emerald-600"}`} />
                        : <Circle className={`h-4 w-4 ${isActive ? "text-sand-100/40" : "text-sand-300"}`} />}
                    </span>
                  </span>
                </button>
              );
            })}
            {filteredSections.length === 0 && (
              <p className="px-3 py-4 text-xs text-muted-foreground" data-testid="tafsir-search-empty">{tr("tafsirReader.noSectionsMatch", { query })}</p>
            )}
          </nav>
        </aside>

        {/* Reading pane */}
        <main className="min-w-0">
          {!active ? (
            <IntroPage meta={meta} onStart={() => selectSection(meta.sections[0].slug)} />
          ) : loading ? (
            <div className="hifz-card p-10 text-center" data-testid="tafsir-section-loading">
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-gold-600" />
              <div className="mt-4 font-display font-semibold text-forest-700">{tr("tafsirReader.preparingThisSection")}</div>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                {tr("tafsirReader.theFirstReaderOfEach")}
              </p>
            </div>
          ) : section ? (
            <SectionPage
              section={section}
              surahNum={surahNum}
              prog={prog}
              checked={checked}
              setChecked={setChecked}
              saveProgress={saveProgress}
              hasNext={!!next}
              onNext={() => next && selectSection(next.slug)}
            />
          ) : null}

          {/* Bottom navigation — Previous / Next section only (UC6).
              Bookmark now lives at the top of the section; "Mark complete"
              was removed at the client's request. */}
          <div className="mt-8 hifz-card flex items-center justify-between gap-3 flex-wrap" data-testid="tafsir-bottom-nav">
            <button
              onClick={() => selectSection(prev ? prev.slug : null)}
              disabled={!active}
              className="btn-ghost text-sm disabled:opacity-40"
              data-testid="tafsir-prev-btn"
            >
              <ChevronLeft className="h-4 w-4" /> {tr("tafsirReader.previousSection")}
            </button>
            {next ? (
              <button onClick={() => selectSection(next.slug)} className="btn-accent text-sm" data-testid="tafsir-next-btn">
                {tr("tafsirReader.nextSection")} <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <Link to={`/app/hifz/${surahNum}`} className="btn-accent text-sm" data-testid="tafsir-finish-link">
                <GraduationCap className="h-4 w-4" /> {tr("tafsirReader.startMemorizing")}
              </Link>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

/* ---------------- Introduction page ---------------- */
function IntroPage({ meta, onStart }) {
  const { tx, t: tr } = useLanguage();
  const rev = meta.revelation;
  const [openTheme, setOpenTheme] = useState(null); // UN2 — tapped theme summary
  const themesRef = useRef(null);

  // C7 — a tap-opened tooltip (touch devices have no hover) used to have no
  // way to dismiss it except tapping the exact same pill again; tapping
  // anywhere else on the page left it stuck open indefinitely. Close it on
  // any click/tap outside the themes row, mirroring the same
  // outside-click pattern MadaniPage.jsx already uses for its own popover.
  useEffect(() => {
    if (!openTheme) return;
    const onDown = (e) => {
      if (themesRef.current && !themesRef.current.contains(e.target)) setOpenTheme(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [openTheme]);

  return (
    <article className="space-y-6" data-testid="tafsir-intro">
      <div className="hifz-card grain bg-forest-700 border-forest-700 text-sand-100 p-8 sm:p-10">
        <div className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-semibold">{tr("tafsirReader.surahIntroduction", { surah_number: meta.surah_number })}</div>
        <div className="mt-3 flex items-baseline gap-4 flex-wrap">
          <h1 className="font-display font-bold text-4xl">{meta.name_en}</h1>
          {/* ml-auto pushes the Arabic name to the right edge of the header
              row (English stays left) instead of sitting right after it with
              dead space trailing off to the right. */}
          <span className="ayah-arabic text-4xl text-gold-400 ml-auto" dir="rtl">{meta.name_ar}</span>
        </div>
        <div className="mt-1 text-sand-100/80">{tr("tafsirReader.ayahs", { meaning: meta.meaning, total_ayahs: meta.total_ayahs })}</div>
        <p className="mt-5 text-sand-100/85 leading-relaxed max-w-2xl">{meta.name_reason}</p>
      </div>

      <div className="hifz-card" data-testid="tafsir-revelation">
        <SectionHeading icon={Landmark} title={tr("tafsirReader.revelation")} />
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className={`status-pill ${rev.type === "makki" ? "status-learning" : "status-memorized"}`}>
            {rev.type === "makki" ? tr("tadabur.makki") : tr("tadabur.madani")}
          </span>
          {rev.phase && <span className="status-pill status-revising text-xs">{rev.phase}</span>}
          {rev.hijrah_year && <span className="status-pill status-revising text-xs">{rev.hijrah_year}</span>}
        </div>
        <p className="mt-4 text-foreground/80 leading-relaxed">{rev.context}</p>
      </div>

      <div className="hifz-card" data-testid="tafsir-background">
        <SectionHeading icon={ScrollText} title={tr("tafsirReader.historicalBackground")} />
        <div className="mt-4 space-y-3">
          {meta.historical_background.map((p, i) => (
            <p key={i} className="text-foreground/80 leading-relaxed">{p}</p>
          ))}
        </div>
      </div>

      <div className="hifz-card" data-testid="tafsir-virtues">
        <SectionHeading icon={Sparkles} title={tr("tafsirReader.virtues")} />
        <div className="mt-4 space-y-3">
          {meta.virtues.map((v, i) => (
            <div key={i} className="rounded-xl border-l-4 border-gold-500 bg-gold-500/8 bg-sand-200/50 p-4">
              <p className="text-foreground/85 leading-relaxed">{tx(v.text)}</p>
              <div className="mt-2 text-xs font-semibold text-gold-700">— {v.source}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="hifz-card" data-testid="tafsir-themes">
        <SectionHeading icon={Lightbulb} title={tr("tafsirReader.mainThemes")} />
        {/* UN2 — hovering (or tapping, on touch) a theme shows a short
            summary of what it covers in this surah. Tap toggles it open so
            the feature works without a pointer. */}
        <div className="mt-4 flex flex-wrap gap-2" ref={themesRef}>
          {meta.themes.map((t) => {
            const note = meta.theme_notes?.[t];
            const isOpen = openTheme === t;
            return (
              <div key={t} className="relative group/theme">
                <button
                  type="button"
                  onClick={() => note && setOpenTheme(isOpen ? null : t)}
                  className={`px-3.5 py-1.5 rounded-full bg-forest-600/10 text-forest-700 text-sm font-medium border transition ${
                    isOpen ? "border-forest-600/50 bg-forest-600/15" : "border-forest-600/15"
                  } ${note ? "cursor-help" : "cursor-default"}`}
                  title={note || undefined}
                  aria-expanded={note ? isOpen : undefined}
                  data-testid={`tafsir-theme-${t}`}
                >
                  {t}
                </button>
                {/* C7 — a small arrow anchors the tooltip to the pill that
                    triggered it; without one, a wide tooltip sitting above
                    a row of tightly-packed pills reads as ambiguous about
                    which pill it belongs to. Also clamped to the viewport
                    edges (instead of always centering) so it can't spill
                    off-screen for the first/last pill in the row. */}
                {note && (
                  <span
                    role="tooltip"
                    className={`absolute bottom-full mb-2 w-64 max-w-[80vw] rounded-xl bg-forest-700 text-sand-100 text-xs leading-relaxed px-3 py-2.5 shadow-xl z-30 transition-opacity
                      left-1/2 -translate-x-1/2
                      group-first/theme:left-0 group-first/theme:translate-x-0
                      group-last/theme:left-auto group-last/theme:right-0 group-last/theme:translate-x-0
                      ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none group-hover/theme:opacity-100"}`}
                    data-testid={`tafsir-theme-note-${t}`}
                  >
                    {note}
                    <span
                      className="absolute top-full h-2.5 w-2.5 rotate-45 bg-forest-700
                        left-1/2 -translate-x-1/2
                        group-first/theme:left-4 group-first/theme:translate-x-0
                        group-last/theme:left-auto group-last/theme:right-4 group-last/theme:translate-x-0"
                      aria-hidden="true"
                    />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="hifz-card" data-testid="tafsir-objectives">
        <SectionHeading icon={ListChecks} title={tr("tafsirReader.learningObjectives")} />
        <p className="mt-2 text-sm text-muted-foreground">{tr("tafsirReader.byTheEndOfThis")}</p>
        <ul className="mt-3 space-y-2.5">
          {meta.learning_objectives.map((o, i) => (
            <li key={i} className="flex gap-3 text-foreground/85">
              <CheckCircle2 className="h-4.5 w-4.5 h-5 w-5 shrink-0 text-emerald-600 mt-0.5" />
              <span className="leading-relaxed">{o}</span>
            </li>
          ))}
        </ul>
        <button onClick={onStart} className="btn-primary mt-6" data-testid="tafsir-start-reading">
          {tr("tafsirReader.startReading")} <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}

/* ---------------- Theme section page ---------------- */
function SectionPage({ section, surahNum, prog, checked, setChecked, saveProgress, hasNext, onNext }) {
  const { tx, t: tr } = useLanguage();
  const c = section.content || {};
  const dp = c.daily_pointers || {};
  const rangeLabel = section.start === section.end ? tr("common.verseN", { n: section.start }) : tr("common.versesRange", { start: section.start, end: section.end });
  const [note, setNote] = useState(prog.note || "");
  const [refsOpen, setRefsOpen] = useState(false); // UC7 — collapsed by default
  const [notesOpen, setNotesOpen] = useState(false); // C10 — side panel, not embedded in the page
  useEffect(() => { setNote(prog.note || ""); }, [prog.note, section.slug]);

  // C11 — "Listen to this section" used to link to /app/qari, a full page
  // navigation away from the reader; it now plays inline instead, reusing
  // the ayah text this section already has loaded (`section.ayahs`) rather
  // than a separate surah fetch, since the range is always just this
  // section's own start–end.
  const [listenOpen, setListenOpen] = useState(false);
  const [qaris, setQaris] = useState([]);
  const [qari, setQari] = useState(null);
  const [currentAyah, setCurrentAyah] = useState(section.start);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/qaris").then((r) => { setQaris(r.data || []); setQari((r.data || [])[0] || null); }).catch(() => {});
  }, []);

  useEffect(() => { setCurrentAyah(section.start); setPlaying(false); setListenOpen(false); }, [section.slug, section.start]);

  const audioUrl = qari
    ? qari.url_pattern
        .replace("{surah:03d}", String(surahNum).padStart(3, "0"))
        .replace("{ayah:03d}", String(currentAyah).padStart(3, "0"))
    : null;

  const toggleListen = () => {
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    setCurrentAyah(section.start);
    setPlaying(true);
    setTimeout(() => audioRef.current?.play().catch(() => setPlaying(false)), 60);
  };

  const onListenEnded = () => {
    if (currentAyah < section.end) {
      setCurrentAyah(currentAyah + 1);
      setTimeout(() => audioRef.current?.play().catch(() => setPlaying(false)), 60);
    } else {
      setPlaying(false);
      setCurrentAyah(section.start);
    }
  };

  return (
    <article className="space-y-6" data-testid={`tafsir-section-${section.slug}`}>
      {/* Header */}
      <header className="hifz-card p-8">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          {/* UN4 — the arrow signals there are further sections after this
              one; it doubles as the control that opens the next one. */}
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-600 font-semibold inline-flex items-center gap-1">
            {rangeLabel}
            {hasNext && (
              <button
                type="button"
                onClick={onNext}
                className="inline-flex items-center text-gold-600 hover:text-gold-700 transition"
                title={tr("tafsirReader.moreSectionsFollowOpenThe")}
                aria-label={tr("tafsirReader.goToTheNextSection")}
                data-testid="tafsir-more-sections-arrow"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {/* UC6 — Bookmark moved up here, out of the bottom bar. */}
          {/* C13 — icon-only everywhere; this is the only bookmark control
              in the app (see BookmarkIcon.jsx's own comment), so it's also
              the only place this needed changing. */}
          <button
            onClick={() => saveProgress({ bookmarked: !prog.bookmarked })}
            className={`inline-flex items-center justify-center h-8 w-8 rounded-lg border transition shrink-0 ${
              prog.bookmarked
                ? "border-gold-500/50 bg-gold-500/15 text-gold-600"
                : "border-sand-300 text-foreground/60 hover:bg-sand-200"
            }`}
            title={prog.bookmarked ? tr("tafsirReader.bookmarked") : tr("tafsirReader.bookmarkThisSection")}
            aria-label={prog.bookmarked ? tr("tafsirReader.removeBookmark") : tr("tafsirReader.bookmarkThisSection")}
            aria-pressed={!!prog.bookmarked}
            data-testid="tafsir-bookmark-btn"
          >
            {prog.bookmarked ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
          </button>
        </div>
        <h1 className="mt-2 font-display font-semibold text-3xl text-forest-700" data-testid="tafsir-section-title">{tx(section.title)}</h1>
        {c.overview && <p className="mt-3 text-foreground/75 leading-relaxed">{c.overview}</p>}
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setListenOpen((v) => !v)}
            className={`btn-ghost text-sm ${listenOpen ? "bg-forest-700 text-sand-100 border-forest-700" : ""}`}
            data-testid="tafsir-listen-toggle"
          >
            <Headphones className="h-4 w-4" /> {listenOpen ? tr("tafsirReader.listening") : tr("tafsirReader.listenToThisSection")}
          </button>
        </div>
        {listenOpen && (
          <div className="mt-3 rounded-xl border border-sand-300 bg-sand-100/60 p-3 flex items-center gap-2 flex-wrap" data-testid="tafsir-listen-inline">
            <button
              type="button"
              onClick={toggleListen}
              disabled={!audioUrl}
              className="btn-primary text-sm disabled:opacity-40 shrink-0"
              data-testid="tafsir-listen-play"
            >
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {playing ? tr("myQuran.pause") : tr("myQuran.play")}
            </button>
            <select
              value={qari?.id || ""}
              onChange={(e) => setQari(qaris.find((x) => x.id === e.target.value))}
              className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm"
              data-testid="tafsir-listen-qari"
            >
              {qaris.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
            <span className="text-xs text-muted-foreground" data-testid="tafsir-listen-current">
              {tr("tafsirReader.ayahOf", { currentAyah, start: section.start, end: section.end })}
            </span>
            <button
              type="button"
              onClick={() => { setListenOpen(false); audioRef.current?.pause(); setPlaying(false); }}
              className="p-2 rounded-lg hover:bg-sand-200 ml-auto shrink-0"
              aria-label={tr("myQuran.closeListen")}
              data-testid="tafsir-listen-close"
            >
              <X className="h-4 w-4" />
            </button>
            {audioUrl && (
              <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={onListenEnded}
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                data-testid="tafsir-listen-audio"
              />
            )}
          </div>
        )}
      </header>

      {/* Arabic + translation, book style */}
      <div className="hifz-card p-8 sm:p-10" data-testid="tafsir-ayahs">
        <div className="ayah-arabic text-right text-2xl sm:text-3xl leading-[2.6] text-foreground/90" dir="rtl">
          {section.ayahs.map((a) => (
            <span key={a.n}>
              {a.ar} <span className="text-gold-600 mx-1">﴿{a.n}﴾</span>{" "}
            </span>
          ))}
        </div>
        <div className="ornamental-line my-6" />
        <div className="space-y-3">
          {section.ayahs.map((a) => (
            <p key={a.n} className="text-foreground/80 leading-relaxed">
              <span className="text-xs font-semibold text-gold-700 mr-2">{surahNum}:{a.n}</span>
              {a.en}
            </p>
          ))}
        </div>
      </div>

      {/* Explanation */}
      {c.explanation?.length > 0 && (
        <div className="hifz-card p-8 sm:p-10" data-testid="tafsir-explanation">
          <SectionHeading icon={BookOpen} title={tr("tafsirReader.explanation")} />
          <div className="mt-5 space-y-4">
            {c.explanation.map((p, i) => (
              <p key={i} className="text-foreground/85 leading-[1.9] text-[15px]">{p}</p>
            ))}
          </div>
        </div>
      )}

      {/* Key lessons callouts */}
      {c.key_lessons?.length > 0 && (
        <div className="hifz-card" data-testid="tafsir-lessons">
          <SectionHeading icon={Lightbulb} title={tr("tafsirReader.keyLessons")} />
          <div className="mt-4 space-y-3">
            {c.key_lessons.map((l, i) => (
              <div key={i} className="rounded-xl border-l-4 border-forest-600 bg-forest-600/6 bg-sand-200/50 p-4 text-foreground/85 leading-relaxed">
                {l}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vocabulary */}
      {c.vocabulary?.length > 0 && (
        <div className="hifz-card" data-testid="tafsir-vocabulary">
          <SectionHeading icon={ScrollText} title={tr("tafsirReader.vocabulary")} />
          <div className="mt-4 grid gap-3">
            {c.vocabulary.map((v, i) => (
              <div key={i} className="rounded-xl border border-sand-300 bg-sand-200/40 p-4">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="ayah-arabic text-xl text-forest-700">{v.word_ar}</span>
                  <span className="text-xs text-muted-foreground italic">{v.transliteration}</span>
                </div>
                <div className="mt-1.5 font-medium text-sm">{v.meaning}</div>
                {v.usage && <div className="mt-1 text-xs text-muted-foreground leading-relaxed">{v.usage}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reflection */}
      {(c.reflection_questions?.length > 0 || c.action_points?.length > 0) && (
        <div className="hifz-card" data-testid="tafsir-reflection">
          <SectionHeading icon={Sparkles} title={tr("tafsirReader.reflection")} />
          {c.reflection_questions?.length > 0 && (
            <ul className="mt-4 space-y-2.5">
              {c.reflection_questions.map((q, i) => (
                <li key={i} className="flex gap-3 text-foreground/85">
                  <span className="h-6 w-6 shrink-0 rounded-full border border-gold-500 text-gold-600 grid place-items-center text-xs font-semibold">{i + 1}</span>
                  <span className="leading-relaxed">{q}</span>
                </li>
              ))}
            </ul>
          )}
          {c.action_points?.length > 0 && (
            <>
              <div className="mt-5 text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">{tr("tafsirReader.actionPoints")}</div>
              <ul className="mt-2 space-y-2">
                {c.action_points.map((a, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-foreground/80">
                    <ChevronRight className="h-4 w-4 shrink-0 text-forest-700 mt-0.5" /> {a}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Daily pointers — whichever of Asma / Seerah / Dua / Do's / Don'ts
          actually apply to THESE ayahs (most sections won't have all five;
          empty categories are simply omitted, never invented — see
          ai_service.TAFSIR_SECTION_SYSTEM). */}
      {(c.daily_action_summary || dp.asma?.length > 0 || dp.seerah?.length > 0 || dp.dua?.length > 0 || dp.commands?.length > 0 || dp.prohibitions?.length > 0) && (
        <div className="hifz-card" data-testid="tafsir-daily-pointers">
          <SectionHeading icon={Sunrise} title={tr("tafsirReader.todaySTakeaway")} />

          {c.daily_action_summary && (
            <div className="mt-4 rounded-xl border-l-4 border-gold-500 bg-gold-500/8 p-4 text-foreground/85 leading-relaxed" data-testid="tafsir-daily-summary">
              {c.daily_action_summary}
            </div>
          )}

          {dp.asma?.length > 0 && (
            <div className="mt-5" data-testid="tafsir-daily-asma">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold flex items-center gap-1.5">
                <Star className="h-3.5 w-3.5" /> {tr("tafsirReader.allahSNames")}
              </div>
              <div className="mt-2 space-y-2">
                {dp.asma.map((n, i) => (
                  <div key={i} className="rounded-xl border border-sand-300 bg-sand-200/40 p-3">
                    <div className="flex items-baseline gap-2">
                      {n.name_ar && <span className="ayah-arabic text-lg text-forest-700" dir="rtl">{n.name_ar}</span>}
                      {n.name_en && <span className="text-sm font-medium">{n.name_en}</span>}
                    </div>
                    {n.reflection && <div className="mt-1 text-sm text-foreground/80 leading-relaxed">{n.reflection}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {dp.seerah?.length > 0 && (
            <div className="mt-5" data-testid="tafsir-daily-seerah">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold flex items-center gap-1.5">
                <History className="h-3.5 w-3.5" /> {tr("tafsirReader.seerahConnection")}
              </div>
              <div className="mt-2 space-y-2">
                {dp.seerah.map((s, i) => (
                  <div key={i} className="rounded-xl border border-sand-300 bg-sand-200/40 p-3">
                    {s.incident && <div className="text-sm font-medium">{s.incident}</div>}
                    {s.lesson && <div className="mt-1 text-sm text-foreground/80 leading-relaxed">{s.lesson}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {dp.dua?.length > 0 && (
            <div className="mt-5" data-testid="tafsir-daily-dua">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold flex items-center gap-1.5">
                <HandHeart className="h-3.5 w-3.5" /> {tr("tafsirReader.dua")}
              </div>
              <div className="mt-2 space-y-2">
                {dp.dua.map((d, i) => (
                  <div key={i} className="rounded-xl border border-sand-300 bg-sand-200/40 p-3">
                    {d.dua_ar && <div className="ayah-arabic text-lg text-forest-700 text-right" dir="rtl">{d.dua_ar}</div>}
                    {d.dua_en && <div className="mt-1 text-sm italic text-foreground/80">{d.dua_en}</div>}
                    {d.guidance && <div className="mt-1.5 text-sm text-foreground/80 leading-relaxed">{d.guidance}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(dp.commands?.length > 0 || dp.prohibitions?.length > 0) && (
            <div className="mt-5 grid gap-4">
              {dp.commands?.length > 0 && (
                <div data-testid="tafsir-daily-dos">
                  <div className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold flex items-center gap-1.5">
                    <ThumbsUp className="h-3.5 w-3.5" /> {tr("tafsirReader.do")}
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {dp.commands.map((cmd, i) => (
                      <li key={i} className="flex gap-2 text-sm text-foreground/80">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 mt-0.5" /> {cmd}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {dp.prohibitions?.length > 0 && (
                <div data-testid="tafsir-daily-donts">
                  <div className="text-xs uppercase tracking-[0.18em] text-red-700 font-semibold flex items-center gap-1.5">
                    <Ban className="h-3.5 w-3.5" /> {tr("tafsirReader.donT")}
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {dp.prohibitions.map((p, i) => (
                      <li key={i} className="flex gap-2 text-sm text-foreground/80">
                        <Ban className="h-4 w-4 shrink-0 text-red-600 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Life application checklist */}
      {c.life_application?.length > 0 && (
        <div className="hifz-card" data-testid="tafsir-application">
          <SectionHeading icon={ListChecks} title={tr("tafsirReader.lifeApplication")} />
          <p className="mt-2 text-sm text-muted-foreground">{tr("tafsirReader.howCanIPracticeThis")}</p>
          <ul className="mt-3 space-y-2">
            {c.life_application.map((item, i) => (
              <li key={i}>
                <button
                  onClick={() => setChecked((p) => ({ ...p, [i]: !p[i] }))}
                  className="flex items-start gap-3 text-left w-full group"
                  data-testid={`tafsir-check-${i}`}
                >
                  {checked[i]
                    ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600 mt-0.5" />
                    : <Circle className="h-5 w-5 shrink-0 text-sand-300 group-hover:text-gold-500 mt-0.5" />}
                  <span className={`leading-relaxed ${checked[i] ? "line-through text-muted-foreground" : "text-foreground/85"}`}>{item}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* "Now make it yours" moved to the Memorize flow (replaces its old
          Reflect step) so the practice CTA sits right before Record + AI
          Feedback instead of duplicating them here — see components/CLAUDE.md
          Batch 4 notes. Section still links onward via the memorize-link
          from Track Memorization → SurahView → Memorize, unchanged. */}

      {/* Notes — C10: a side panel (sticky-note-style tab, always reachable
          from the edge of the screen) instead of a full-width box sitting
          in the reading flow between Life Application and References. */}
      <button
        type="button"
        onClick={() => setNotesOpen(true)}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-40 flex flex-col items-center gap-1.5 rounded-l-xl border border-r-0 border-gold-500/40 bg-gold-500/15 hover:bg-gold-500/25 backdrop-blur-sm px-2 py-3 shadow-md transition"
        data-testid="tafsir-notes-tab"
        aria-label={tr("tafsirReader.openMyNotes")}
      >
        <NotebookPen className="h-4 w-4 text-gold-700" />
        <span className="text-[10px] font-semibold text-gold-700 [writing-mode:vertical-rl] tracking-wide">{tr("tafsirReader.myNotes")}</span>
        {note.trim() && <span className="h-1.5 w-1.5 rounded-full bg-forest-600" data-testid="tafsir-notes-tab-dot" />}
      </button>
      <Sheet open={notesOpen} onOpenChange={setNotesOpen}>
        <SheetContent side="right" data-testid="tafsir-notes-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <NotebookPen className="h-4 w-4 text-gold-600" /> {tr("tafsirReader.myNotes")}
            </SheetTitle>
          </SheetHeader>
          <p className="mt-1 text-xs text-muted-foreground">{tx(section.title)}</p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={10}
            placeholder={tr("tafsirReader.whatMovedYouInThis")}
            className="mt-4 w-full rounded-xl border border-sand-300 bg-white p-3 text-sm leading-relaxed"
            data-testid="tafsir-note-input"
          />
          <button
            onClick={() => saveProgress({ note }).then(() => toast.success(tr("tafsirReader.noteSaved")))}
            className="btn-ghost text-sm mt-3"
            data-testid="tafsir-note-save"
          >
            {tr("tafsirReader.saveNote")}
          </button>
        </SheetContent>
      </Sheet>

      {/* References — collapsed behind a single button (UC7) */}
      <div className="hifz-card" data-testid="tafsir-references">
        <button
          type="button"
          onClick={() => setRefsOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 text-left"
          aria-expanded={refsOpen}
          data-testid="tafsir-references-toggle"
        >
          {/* Same look as <SectionHeading>, but span-based — a heading
              element isn't valid phrasing content inside a <button>. */}
          <span className="font-display font-semibold text-xl text-forest-700 inline-flex items-center gap-2.5">
            <span className="h-8 w-8 rounded-lg bg-sand-200 grid place-items-center text-gold-600">
              <GraduationCap className="h-4 w-4" />
            </span>
            {tr("tafsirReader.references")}
          </span>
          <ChevronDown className={`h-4 w-4 text-forest-700 shrink-0 transition-transform ${refsOpen ? "rotate-180" : ""}`} />
        </button>
        {refsOpen && (
          <div data-testid="tafsir-references-body">
            <ul className="mt-3 space-y-1.5">
              {(section.references || []).map((r) => (
                <li key={r.name} className="text-sm text-foreground/75 flex items-center gap-2">
                  <span className={`status-pill text-[10px] ${r.role === "primary" ? "status-memorized" : "status-revising"}`}>
                    {r.role === "primary" ? tr("tafsirReader.primary") : tr("tafsirReader.supporting")}
                  </span>
                  {r.name}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              {tr("tafsirReader.theArchitectureSupportsMultipleTafsir")}
            </p>
          </div>
        )}
      </div>
    </article>
  );
}

function SectionHeading({ icon: Icon, title }) {
  return (
    <h2 className="font-display font-semibold text-xl text-forest-700 inline-flex items-center gap-2.5">
      <span className="h-8 w-8 rounded-lg bg-sand-200 grid place-items-center text-gold-600">
        <Icon className="h-4 w-4" />
      </span>
      {title}
    </h2>
  );
}
